import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Divider,
  IconButton,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LocationOn,
  Phone,
  Email,
  Schedule,
  Facebook,
} from '@mui/icons-material';
import krawanetterLogo from '../assets/logos/kranawetter-logo.png';

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  const SCROLL_OFFSET_PX = 90;
  const scrollToSection = (sectionId) => {
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Scroll after navigation
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const y = element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET_PX;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET_PX;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: 'primary.dark',
        color: 'white',
        pt: 6,
        pb: 3,
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                component="img"
                src={krawanetterLogo}
                alt="Kranawetter Transport Logo"
                sx={{
                  height: 32,
                  width: 'auto',
                  mr: 2,
                  maxWidth: 120,
                  objectFit: 'contain'
                }}
              />
              <Typography variant="h3" fontWeight="bold">
                Kranawetter Transport
              </Typography>
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2, 
                color: 'rgba(255, 255, 255, 0.95)', // Better contrast than opacity 0.9
                lineHeight: 1.6
              }}
            >
              Your strategic transportation partner since 2013. We deliver exceptional freight solutions 
              with a modern fleet of over 100 vehicles, driving business growth for customers 
              across the Midwest and throughout the United States.
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <LocationOn sx={{ fontSize: 16, mr: 1 }} />
              WBE Certified Business
            </Typography>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={4}>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
              Contact Us
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                <LocationOn sx={{ fontSize: 16, mr: 1 }} />
              <Link 
                href="https://www.google.com/maps/place/Kranawetter+Transport/data=!4m2!3m1!1s0x0:0xae239feea11a9be8?sa=X&ved=1t:2428&ictx=111"
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                  '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                  }
                }}
              >
                15628 State Highway 72
              </Link>
              </Typography>
              <Link 
                href="https://www.google.com/maps/place/Kranawetter+Transport/data=!4m2!3m1!1s0x0:0xae239feea11a9be8?sa=X&ved=1t:2428&ictx=111"
                target="_blank"
                rel="noopener noreferrer"
                color="inherit"
                underline="always"
                variant="body2"
                sx={{ 
                ml: 3, 
                mb: 2,
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                }
                }}
              >
                Patton, MO 63662
              </Link>
            </Box>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <Phone sx={{ fontSize: 16, mr: 1 }} />
              <Link 
                href="tel:+15738662866" 
                color="inherit" 
                underline="always"
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                  '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                  }
                }}
              >
                (573) 866-2866
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <Email sx={{ fontSize: 16, mr: 1 }} />
              Flatbed/Specialized Dispatch:{' '}
              <Link 
                href="mailto:dispatch@kranawettertransport.com" 
                color="inherit" 
                underline="always"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.95)',
                  textDecorationColor: 'rgba(255, 255, 255, 0.5)',
                  ml: 0.5,
                  '&:hover': { 
                    color: '#ffffff',
                    textDecorationColor: '#ffffff'
                  }
                }}
              >
                dispatch@kranawettertransport.com
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', ml: 3 }}>
              Van Dispatch:{' '}
              <Link 
                href="mailto:vandispatch@kranawettertransport.com" 
                color="inherit" 
                underline="always"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.95)',
                  textDecorationColor: 'rgba(255, 255, 255, 0.5)',
                  ml: 0.5,
                  '&:hover': { 
                    color: '#ffffff',
                    textDecorationColor: '#ffffff'
                  }
                }}
              >
                vandispatch@kranawettertransport.com
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
              <Schedule sx={{ fontSize: 16, mr: 1 }} />
              Mon-Fri: 7:00 AM - 5:00 PM
            </Typography>

            
          </Grid>

          {/* Services */}
          <Grid item xs={12} md={4}>
            <Typography variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
              Our Services
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Link 
                href="#services" 
                color="inherit" 
                underline="always"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                  fontWeight: 400
                }}
              >
                Flatbed Hauling
              </Link>
              <Link 
                href="#services" 
                color="inherit" 
                underline="always"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                  fontWeight: 400
                }}
              >
                Dry Van Transport
              </Link>
              <Link 
                href="#services" 
                color="inherit" 
                underline="always"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('services');
                }}
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                  fontWeight: 400
                }}
              >
                Over-Dimensional Loads
              </Link>
              <Link 
                component={RouterLink}
                to="/cdl-jobs"
                color="inherit" 
                underline="always"
                sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                  fontWeight: 400
                }}
              >
                CDL Driver Opportunities
              </Link>
            </Box>
            
            {/* Social Media */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Follow Us
              </Typography>
              <Box>
                <IconButton 
                  component="a"
                  href="https://www.facebook.com/kranawettertransport"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'white', p: 0.5, mr: 1 }}
                  aria-label="Facebook"
                >
                  <Facebook />
                </IconButton>
                
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />

        {/* Bottom Footer */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.95)',
              fontWeight: 400
            }}
          >
            © {currentYear} Kranawetter Transport. All rights reserved. DOT: 2077730
            <br />
            Website technology by             <Link 
              href="https://www.hammerlanetech.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              color="inherit"
              underline="always"
              sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                }
              }}
            >
              Hammer Lane Tech
            </Link>
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Link
              href="https://portal.kranawettertransport.com"
              color="inherit"
              underline="always"
              variant="body2"
              sx={{
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': {
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                fontWeight: 400
              }}
            >
              Driver login
            </Link>
            <Link 
              component={RouterLink}
              to="/about"
              color="inherit" 
              underline="always"
              variant="body2"
              sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                fontWeight: 400
              }}
            >
              About
            </Link>
            <Link 
              component={RouterLink}
              to="/privacy"
              color="inherit" 
              underline="always"
              variant="body2"
              sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                fontWeight: 400
              }}
            >
              Privacy Policy
            </Link>
            <Link 
              component={RouterLink}
              to="/terms"
              color="inherit" 
              underline="always"
              variant="body2"
              sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                fontWeight: 400
              }}
            >
              Terms of Service
            </Link>
            <Link 
              component={RouterLink}
              to="/safety"
              color="inherit" 
              underline="always"
              variant="body2"
              sx={{ 
                color: '#ffffff',
                textDecorationColor: '#ffffff',
                '&:hover': { 
                  color: '#ffffff',
                  textDecorationColor: '#ffffff'
                },
                fontWeight: 400
              }}
            >
              Safety
            </Link>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 